import { useQuery } from '@tanstack/react-query';
import type { Guide, IntroVideo, Supply } from './types';

// iFixit's stock "no image" placeholder graphics, e.g.
// .../guide/NoImageFixie_96x96.jpg or .../guide/NoImage_140x105.jpg.
const PLACEHOLDER_THUMBNAIL = /NoImage(Fixie)?_/;

// Same endpoint the vanilla boot gate hit (embed.v5.data.js): the guide plus
// step comments and per-step video timestamps, keyed off the App globals the
// PHP shell injects. The default react-query fn (react-query-initialize) turns
// queryKey[0] into `GET /api/2.0/<key>`.
function guideQueryKey(): [string] {
   const params = new URLSearchParams({ stepComments: '1', videoInfo: '1' });
   if (App.langid) {
      params.set('langid', String(App.langid));
   }
   if (App.guide_revisionid) {
      params.set('revisionid', String(App.guide_revisionid));
   }
   return [`guides/${App.guideid}?${params.toString()}`];
}

export function useGuide() {
   return useQuery<Guide, Error, Guide>({
      queryKey: guideQueryKey(),
      select: amendGuide,
   });
}

/**
 * Fold the raw api/2.0 guide into the shape the embed renders — the React analog
 * of amendGuideData() in embed.v5.data.js. Pure and copy-on-write so react-query
 * can memoize it via `select`.
 */
function amendGuide(raw: Guide): Guide {
   const steps = raw.steps.map((step, index) => ({ ...step, number: index + 1 }));

   // A chaptered video guide: one video split across steps by per-step
   // timestamps; the step text swaps as the persistent player advances.
   const isVideoGuide = Boolean(
      raw.video_info &&
         raw.video_info.type !== 'none' &&
         raw.video_info.timestamps &&
         raw.video_info.timestamps.length > 0
   );

   // A guide whose entire content is a single embedded video with no steps.
   const isVideoOnly = steps.length === 0 && Boolean(raw.intro_video_url || raw.intro_video);

   if (isVideoGuide && raw.video_info?.timestamps) {
      const byStepid = new Map(raw.video_info.timestamps);
      for (const step of steps) {
         step.videoTimestamp = byStepid.get(step.stepid) ?? null;
      }
   }

   return {
      ...raw,
      steps,
      isVideoGuide,
      isVideoOnly,
      introVideo: normalizeIntroVideo(raw.intro_video),
      tools: normalizeSupplyThumbnails(raw.tools),
      parts: normalizeSupplyThumbnails(raw.parts),
      url: App.guideViewUrl ?? raw.url,
   };
}

// The api/2.0 `intro_video` field arrives as null, an array of hosted
// renditions ({url, mime}), or a remote oEmbed object ({html}). Collapse to one
// shape so consumers branch on `kind` instead of sniffing the wire union.
function normalizeIntroVideo(introVideo: unknown): IntroVideo | null {
   if (!introVideo) {
      return null;
   }

   if (Array.isArray(introVideo)) {
      const sources = introVideo
         .filter(r => r && r.url && r.mime)
         .map(r => ({ src: String(r.url), type: String(r.mime) }));
      return sources.length ? { kind: 'hosted', sources } : null;
   }

   const html = (introVideo as { html?: string }).html;
   return html ? { kind: 'embed', html } : null;
}

// Externally-linked supplies with no real image come back pointing at iFixit's
// stock "no image" graphic; blank those so the UI renders its own placeholder.
function normalizeSupplyThumbnails(items: Supply[] | undefined): Supply[] {
   return (items ?? []).map(item =>
      typeof item.thumbnail === 'string' && PLACEHOLDER_THUMBNAIL.test(item.thumbnail)
         ? { ...item, thumbnail: null }
         : item
   );
}
